export const guestHelp = [
  {
    section: 'Getting started',
    items: [
      { q: 'Who can reserve seating?', a: 'Seating is available to registered Acqualina hotel guests and Acqualina residents. You will be asked to verify your stay or residence before you can book.' },
      { q: 'How do I verify as a hotel guest?', a: 'Choose "Reserve Now", then the Hotel Guest tab. Enter either your hotel confirmation number, or your room number together with your last name. We check your details against the hotel reservation system in real time.' },
      { q: 'How do I verify as a resident?', a: 'Choose "Reserve Now", then the Resident tab. Enter your unit number and last name (the building field is optional). If your details are not recognized, please contact the front desk to update your resident record.' },
      { q: 'My verification failed. What should I do?', a: 'Double-check the spelling of your last name and your room or confirmation number. Hotel guests must currently be checked in. If it still fails, please contact the front desk.' },
    ],
  },
  {
    section: 'Booking a seat',
    items: [
      { q: 'How do I book?', a: 'After verifying, pick an area (for example Beach Sofas or Front Lawn), choose a date from the date bar, then tap an available (green) seat on the map or grid. Press "Book Now", add your email and/or phone number, and confirm.' },
      { q: 'What do the seat colors mean?', a: 'Green = available, red = reserved, yellow = on hold, gray = closed or not bookable for this date, and "Your seat" marks a seat you have already reserved.' },
      { q: 'Why is tomorrow selected by default?', a: 'After 2:00 PM Eastern time, the booking page opens on the next day. You can still select today from the date bar if seats are available.' },
      { q: 'Can I book more than one seat per day?', a: 'No. Each guest or residence can hold one seat per day. If you already have a booking for a date, you will see it at the top of the page with options to change or cancel it.' },
      { q: 'How far ahead can I book?', a: 'Paid seating can be reserved up to 30 days in advance. Free seating has shorter limits, described below.' },
    ],
  },
  {
    section: 'Free seating',
    items: [
      { q: 'When can I book a free chair?', a: 'Free chairs are generally bookable for the same day only, up to a daily cutoff time set by the resort. Some dates may allow limited advance booking.' },
      { q: 'Why can\'t I select a free chair?', a: 'It may already be reserved, the daily cutoff may have passed, or the date is outside the free booking window. Hover over or tap the seat to see the reason.' },
    ],
  },
  {
    section: 'Pricing & payment',
    items: [
      { q: 'How are prices set?', a: 'Prices vary by seat type, date and resort demand. The price shown on the seat when you book is the price saved with your reservation.' },
      { q: 'When will I be charged?', a: 'Paid reservations are placed as an authorization only. Your booking shows as "Reservation Received" until it is approved by the resort.' },
    ],
  },
  {
    section: 'Managing your reservation',
    items: [
      { q: 'How do I view my booking?', a: 'Use "Manage my reservation" on the home page. Enter your confirmation code and last name, or your room/unit number and last name, to see all upcoming bookings.' },
      { q: 'How do I change my seat or date?', a: 'On the Manage page, press "Change". Your current seat is released and you are taken back to booking so you can pick a new seat or date.' },
      { q: 'How do I cancel?', a: 'On the Manage page (or the booking page banner), press "Cancel" and confirm. The seat is released immediately.' },
      { q: 'I lost my confirmation code.', a: 'Look it up on the Manage page using your room or unit number and last name. It is also included in your confirmation email or text message.' },
    ],
  },
  {
    section: 'Weather & notifications',
    items: [
      { q: 'What happens in bad weather?', a: 'The resort monitors wind, rain and lightning. If conditions affect your reservation, you will receive an alert by text (if you opted in) with next steps.' },
      { q: 'How do I get text messages?', a: 'Enter your phone number and tick the SMS consent box when booking. Message and data rates may apply; reply STOP to opt out at any time.' },
    ],
  },
];

export const staffHelp = [
  {
    section: 'Getting around',
    items: [
      { q: 'What does each menu item do?', a: 'Dashboard: overview. Properties: resort details and free chair settings. Maps: upload area maps. Inventory / Inventory Types: seats and their categories. Reservations: view, approve, cancel. Blackouts: close areas or seats. Pricing: date and occupancy rules. Users: staff accounts. Reports: insights. Settings: integrations and message templates.' },
      { q: 'Who can sign in to the admin portal?', a: 'Only staff accounts created under Users. Guests and residents never sign in here; they verify on the public booking site.' },
    ],
  },
  {
    section: 'Reservations',
    items: [
      { q: 'How do I find a reservation?', a: 'Open Reservations and use the status and date filters. Each card shows guest, seat, room/unit, date and status. Press "View" for full details including hotel confirmation number.' },
      { q: 'What do the statuses mean?', a: 'Pending authorization: paid booking awaiting approval. Confirmed: active booking (free bookings confirm instantly). Cancelled: released by guest or staff. Checked in / No show: on-the-day outcomes.' },
      { q: 'How do I approve or cancel?', a: 'Open a reservation with "View", then press "Approve Reservation" (for pending paid bookings) or "Cancel Reservation". Cancelling frees the seat right away.' },
      { q: 'Can a guest hold more than one seat?', a: 'No. The system allows one active seat per guest per day, matched by room/unit + last name or hotel confirmation number. Booked seats are automatically blocked for everyone else.' },
    ],
  },
  {
    section: 'Inventory & maps',
    items: [
      { q: 'How do I add seats?', a: 'Upload an area map under Maps, then go to Inventory to add items and place them on the map. Set type, capacity, base price and whether the seat is free.' },
      { q: 'How do I take a seat out of service?', a: 'Set its status to out of order in Inventory, or create a specific-item blackout for a date range.' },
    ],
  },
  {
    section: 'Blackouts',
    items: [
      { q: 'How do I close an area or the whole property?', a: 'Go to Blackouts and create a blackout for the full property, an area, an inventory type or a specific seat, with start and end dates. Affected seats show as closed to guests.' },
    ],
  },
  {
    section: 'Pricing',
    items: [
      { q: 'How do date-based rules work?', a: 'Under Pricing > Date Rules, create rules with a date range, scope (all, type, zone or seat) and either a multiplier or fixed rate. Higher priority wins; a fixed rate overrides other rules.' },
      { q: 'How do occupancy/ADR rules work?', a: 'Under Pricing > Occupancy/ADR Rules, set an occupancy % and/or ADR threshold with AND/OR logic and a percentage or flat increase. Hotel occupancy data is pulled from Opera Cloud automatically; use "Refresh" to update now.' },
      { q: 'Can I override occupancy data?', a: 'Yes. Use the manual override on the Occupancy/ADR tab to enter occupancy and ADR values, for example if the hotel system is unavailable.' },
    ],
  },
  {
    section: 'Free chair settings',
    items: [
      { q: 'How do I change free chair limits?', a: 'Go to Properties and open Free Chair Settings. You can enable or disable free booking, set how many hours ahead free chairs can be booked, and set the same-day cutoff time.' },
    ],
  },
  {
    section: 'Integrations & messaging',
    items: [
      { q: 'Which integrations are connected?', a: 'Opera Cloud (hotel guest verification and occupancy), Twilio (text messages) and the National Weather Service (weather alerts). Stripe payments and SMTP2GO email are currently in test mode (simulated).' },
      { q: 'How do I edit messages?', a: 'Go to Settings to edit email and SMS templates. Use placeholders such as {{guest_name}}, {{confirmation_code}}, {{seat_label}} and {{reservation_date}}. Use the test buttons to send yourself a sample.' },
      { q: 'How do weather alerts work?', a: 'Configure wind, rain and lightning thresholds and the alert cutoff time in Settings. The system checks the forecast regularly and texts guests with SMS consent who have bookings today.' },
    ],
  },
  {
    section: 'Testing',
    items: [
      { q: 'Are there test resident accounts?', a: 'Yes, in the preview environment there are 10 test residents (units 1001-1010, e.g. unit 1001 / Anderson). Disable them before going live.' },
    ],
  },
];
